<template>
    <div v-if="isTopbarVisible" class="layout-topbar">
        <div class="tw:flex tw:sm:gap-[2rem]">
            <div v-if="user && hasSideNav && !hideMenuButton" class="tw:flex tw:justify-center tw:sm:w-[6rem]">
                <button
                    ref="menubutton"
                    class="topbar-menubutton tw:bg-transparent tw:border-none tw:text-[var(--text-color)]"
                    type="button"
                    @click="handleMenuButtonClick"
                >
                    <span />
                </button>
            </div>

            <router-link
                :to="{ name: 'home' }"
                class="edverum-logo-link"
                aria-label="Home"
            >
                <edverum-logo id="application_logo" class="is-logo" />
            </router-link>
        </div>

        <ul class="topbar-menu">
            <topbar-links />
        </ul>

        <div class="topbar-actions">
            <global-search
                v-if="user && initialState('GlobalSearchEnabled', false)"
                class="tw:flex tw:items-center"
            />
            <div v-if="user && !shouldHideNavigation">
                <alert-notification
                    v-if="$gate.allows('viewAny', 'Message') && showNotificationBell"
                    class="navbar-item p-none mobile"
                />
            </div>
            <div class="topbar-profile">
                <div
                    v-if="user"
                    class="tw:relative tw:flex tw:items-center tw:gap-2"
                    @click="toggleUserMenu"
                    @mouseenter="showUserMenu"
                    @mouseleave="hideUserMenu"
                    @focusin="showUserMenu"
                    @focusout="hideUserMenu"
                >
                    <button class="layout-topbar-action">
                        <user-avatar
                            :title="user?.name"
                            :img-url="user?.photo_url"
                            size="32x32"
                            :initials="user?.initials"
                            use-initials-when-no-image
                        />
                    </button>
                    <p class="tw:font-semibold tw:hidden tw:lg:block">{{ user?.name }}</p>
                    <i class="uil uil-angle-down tw:text-base" />
                    <Menu
                        v-if="userMenuVisible"
                        :model="userMenuItems"
                        class="tw:absolute tw:right-0 tw:top-full tw:mt-2 tw:min-w-48"
                        :pt="{
                            root: { class: 'tw:shadow-lg tw:rounded-border' },
                        }"
                    >
                        <template #item="{ item, props: itemProps }">
                            <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
                                <a
                                    v-bind="itemProps.action"
                                    :href="href"
                                    style="color: inherit; text-decoration: none; font-weight: 500"
                                    @click="event => navigateAndCloseSidebar(event, navigate, hideUserMenu)"
                                >
                                    <i v-if="item.icon" :class="item.icon" />
                                    <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                                </a>
                            </router-link>
                            <a
                                v-else
                                v-bind="itemProps.action"
                                style="color: inherit; text-decoration: none; font-weight: 500"
                            >
                                <i v-if="item.icon" :class="item.icon" />
                                <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                            </a>
                        </template>
                    </Menu>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import intersection from 'lodash/intersection.js';
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AlertNotification from '@/components/alerts/AlertNotification.vue';
import GlobalSearch from '@/components/app/GlobalSearch.vue';
import UserAvatar from '@/components/elements/Avatar.vue';
import EdverumLogo from '@/components/elements/EdverumLogo.vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { initialState } from '@/Helpers.js';
import Page from '@/models/pages/Page.js';
import User from '@/models/User.js';
import UserSetting from '@/models/UserSetting.js';
import axios from '@/plugins/axios.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import { gate } from '@/plugins/vue-gate/Index.js';
import Utilities from '@/utilities/Utilities.js';
import { useLayout } from './composables/layout.js';
import TopbarLinks from './navigation/TopbarLinks.vue';

const {
    toggleMenu,
    closeMenu,
    toggleSidebarSummary,
    tabs,
    closeTab,
    isDarkTheme,
    toggleConfigSidebar,
    isTopbarVisible,
} = useLayout();
const { t: translate } = useI18n();
const router = useRouter();
const store = useStore();
const instance = getCurrentInstance();
const { $hasFeature } = instance.appContext.config.globalProperties;

// Corporate desktop relocates the sidebar collapse/expand control INTO the
// sidebar header (see AppSidebar), so the top-bar hamburger is hidden there.
// Mobile/tablet still need it to open the drawer; stanbridge keeps it at every
// viewport. Mirrors AppLayout's pattern of wrapping the global $isMobile/
// $isTablet props in computeds so they stay reactive to window resizes.
const isCorporate = isCorporateClient();
const $isMobile = computed(() => instance?.appContext.config.globalProperties.$isMobile);
const $isTablet = computed(() => instance?.appContext.config.globalProperties.$isTablet);
const hideMenuButton = computed(() => isCorporate && !$isMobile.value && !$isTablet.value);

const props = defineProps({
    shouldHideNavigation: Boolean,
    hasSideNav: Boolean,
});

const user = computed(() => {
    return User.query().with(Gate.userRelationships()).find(store.state.Auth.user?.id);
});

function onCloseTab(index) {
    if (tabs.value.length > 1) {
        if (index === tabs.value.length - 1) router.push(tabs.value[tabs.value.length - 2].to);
        else router.push(tabs.value[index + 1].to);
    } else {
        router.push('/');
    }
    closeTab(index);
}

// Data properties
const active = ref(false);
const userActive = ref(false);
const userMenuVisible = ref(false);
const userMenuHideTimeout = ref(null);
const enrolledCourses = ref([]);

const isInDevMode = computed(() => {
    return import.meta.env.MODE === 'development' || window.location.host === 'edverum.test';
});

const showNotificationBell = computed(() => {
    if (store.getters['InstitutionSettings/get']('notifications_enabled')) {
        return true;
    }

    // Corporate clients ship without the `notifications_enabled` institution
    // setting flipped on per-tenant, but every corporate user gets the INBOX
    // feature flag (see App/Features/Inbox::resolve), so the bell is the
    // only in-app surface for inbox alerts. Stanbridge keeps the prior
    // role-only behaviour because admins/owners on stanbridge have always
    // been the implicit allow-list when `notifications_enabled` is off.
    if (isCorporateClient()) {
        return true;
    }

    return !!(store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner'));
});

const showHelpNavLink = computed(() => {
    if (store.getters['InstitutionSettings/get']('support_documentation_enabled')) {
        return true;
    }

    return gate.userCan('view all help articles');
});

const pageAuthorizedRoleIds = computed(() => {
    return Page.getPageAuthorizedRoleIds();
});

const showEditPageNav = computed(() => {
    if (!user.value) return false;

    if (store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner')) return true;

    if (!store.getters['InstitutionSettings/get']('page_enabled')) return false;

    const userGroupRoleIds = user.value.user_groups.map(group => group.role_id);

    return intersection(userGroupRoleIds, pageAuthorizedRoleIds.value).length > 0 || gate.allows('viewAny', 'Page');
});

const canManageFeatureFlags = computed(() => {
    return !!(
        user.value?.is_super_admin ||
        store.getters['User/hasRole']('Admin') ||
        store.getters['User/hasRole']('Owner')
    );
});

const coursesMenuItems = computed(() => {
    const topLevelItems = [
        {
            label: 'Course Templates',
            route: { name: 'course-templates' },
            allowed: gate.allows('viewAny', 'CourseTemplate'),
        },
        {
            label: 'All Courses',
            route: { name: 'courses' },
            allowed: gate.allows('viewAny', 'Course'),
        },
        {
            label: 'Course Codes',
            route: { name: 'course-codes' },
            allowed: gate.allows('viewAny', 'CourseCode'),
        },
        {
            label: 'Meetings',
            route: { name: 'meetings-index' },
            allowed: gate.allows('viewAny', 'Meeting'),
        },
        {
            label: 'Due Dates and Schedules',
            route: { name: 'activities-view-upcoming' },
            allowed: gate.allows('view', 'Activity') && gate.allows('viewAny', 'TimePeriod'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const items = [...topLevelItems];

    // Add separator and enrolled courses section if they exist
    if (enrolledCourses.value.length > 0) {
        if (items.some(item => item.allowed)) {
            items.push({ separator: true });
        }
        items.push({
            label: 'Current Courses',
            route: { name: 'courses' },
            allowed: gate.allows('viewAny', 'Course'),
            small: true,
        });

        // Add enrolled courses sorted alphabetically by title
        const sortedEnrolledCourses = [...enrolledCourses.value].sort((a, b) =>
            (a.course?.title || '').localeCompare(b.course?.title || '')
        );
        sortedEnrolledCourses.forEach(({ course, statuses }) => {
            items.push({
                label: course.title,
                route: { name: 'course-dashboard', params: { courseId: course.id } },
                allowed: true,
                small: true,
            });
        });
    }

    // Filter to only include allowed items
    return items.filter(item => item.separator || item.header || item.allowed);
});

const inboxEnabled = computed(() => {
    return $hasFeature('INBOX');
});

const resourcesMenuItems = computed(() => {
    return [
        {
            label: 'Help & Support',
            route: { name: 'help-home' },
            allowed: showHelpNavLink.value,
        },
        {
            label: 'Inbox',
            route: { name: 'inbox' },
            allowed: inboxEnabled.value,
        },
        {
            label: 'My Notes',
            route: { name: 'notes-home' },
            allowed: true,
        },
    ].sort((a, b) => a.label.localeCompare(b.label));
});

const canSeeProctorDashboardNavLink = computed(() => {
    return (
        gate.userCan("create another user's quiz attempt") || gate.userCanViaGroup("create another user's quiz attempt")
    );
});

const administrationMenuItems = computed(() => {
    const mainSectionItems = [
        {
            label: 'Activity Log',
            route: { name: 'activity-log-index' },
            allowed: gate.userCan('view the activity log'),
        },
        {
            label: 'Impersonation Log',
            route: { name: 'impersonation-log-index' },
            allowed: $hasFeature('USER_IMPERSONATION') && gate.userCan('view the impersonation log'),
        },
        {
            label: 'Attempt Authorizations',
            route: { name: 'attempt-authorizations-index' },
            allowed: gate.allows('viewAny', 'AttemptAuthorization'),
        },
        {
            label: 'Dashboards',
            route: { name: 'dashboards' },
            allowed: true,
        },
        {
            label: 'Evaluation Templates',
            route: { name: 'evaluation-templates-index' },
            allowed: gate.allows('viewAny', 'EvaluationTemplate'),
        },
        {
            label: 'Feature Flags',
            route: { name: 'feature-flags-index' },
            allowed: canManageFeatureFlags.value,
        },
        {
            label: 'Groups',
            route: { name: 'groups-index' },
            allowed: gate.allows('viewAny', 'Group'),
        },
        {
            label: 'Ebooks',
            route: { name: 'ebooks-index' },
            allowed: gate.allows('viewAny', 'Ebook'),
        },
        {
            label: 'Integrations',
            route: { name: 'integrations-index' },
            allowed: gate.allows('viewAny', 'Integrations'),
        },
        {
            label: 'Locations',
            route: { name: 'locations-index' },
            allowed: gate.allows('viewAny', 'Location'),
        },
        {
            label: 'Pages',
            route: { name: 'edit-pages-index' },
            allowed: showEditPageNav.value,
        },
        {
            label: 'People',
            route: { name: 'people-index' },
            allowed: gate.allows('viewAny', 'User'),
        },
        {
            label: 'Institution Settings',
            route: { name: 'institution-settings-profile' },
            allowed: gate.userCan('view an institution setting'),
        },
        {
            label: 'Proctor Dashboard',
            route: { name: 'proctor-dashboard-quiz-lobby' },
            allowed: canSeeProctorDashboardNavLink.value,
        },
        {
            label: 'Remediations',
            route: { name: 'remediations-index' },
            allowed: gate.allows('viewAny', 'Remediation'),
        },
        {
            label: 'Reports',
            route: { name: 'reports-index' },
            allowed: gate.allows('viewAny', 'Report'),
        },
        {
            label: 'Risk Assessment Report',
            route: { name: 'risk-assessment-report' },
            allowed: gate.userCan('view risk assessment report') && !gate.allows('viewAny', 'Report'),
        },
        {
            label: 'Rubric Templates',
            route: { name: 'rubric-bank-index' },
            allowed: gate.allows('viewAny', 'RubricBank'),
        },
        {
            label: 'Slide Decks',
            route: { name: 'slide-decks-index' },
            allowed: gate.allows('viewAny', 'SlideDeck'),
        },
        {
            label: 'Surveys',
            route: { name: 'surveys-index' },
            allowed: gate.allows('viewAny', 'Quiz'),
        },
        {
            label: 'Videos',
            route: { name: 'videos-index' },
            allowed: gate.allows('viewAny', 'Video'),
        },
        {
            label: 'Quizzes',
            route: { name: 'quizzes-index' },
            allowed: gate.allows('viewAny', 'Quiz'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const superAdminSectionItems = [
        {
            label: 'Horizon',
            url: '/horizon',
            target: '_blank',
            allowed: gate.userCan('view horizon'),
        },
        {
            label: 'Pulse',
            url: '/pulse',
            target: '_blank',
            allowed: gate.userCan('view pulse'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    return [...mainSectionItems, { separator: true }, ...superAdminSectionItems];
});

const selected = computed(() => {
    return Number(store.getters['User/activeDarkTheme']());
});

const shouldUseDirectSSOLogin = computed(() => {
    if (isInDevMode.value) {
        return false;
    }

    return initialState('RedirectToSingleSignOn');
});

function logout() {
    const selectApp = document.getElementsByTagName('html')[0];
    selectApp.classList.remove('dark-mode');
    selectApp.classList.remove('invert-black');
    localStorage.removeItem('darkThemePreference');

    window.location.href = '/logout';
}

function navigateAndCloseSidebar($event, navigate, hideDropdown) {
    hideDropdown();
    if (window.innerWidth < 1200) {
        closeMenu();
    }
    navigate($event);
}

const darkModeSetting = computed(() => {
    return UserSetting.findByKey('dark_mode');
});

async function toggleDarkMode() {
    await darkModeSetting.value.toggle();
}

const isImpersonating = computed(() => store.getters['Impersonation/isImpersonating']);
const isImpersonationTransitioning = computed(() => store.getters['Impersonation/isTransitioningImpersonation']);

function stopImpersonation() {
    if (isImpersonationTransitioning.value) {
        return;
    }

    store.dispatch('Impersonation/stopImpersonation');
}

const userMenuItems = computed(() => {
    const items = [
        {
            label: 'Profile',
            icon: 'uil uil-user',
            route: { name: 'me' },
        },
    ];

    // Corporate users get a `Settings` entry between Profile and Logout.
    // For now it routes to the same destination as Profile (`{ name: 'me' }`)
    // until the dedicated corporate settings flow lands; once that route
    // exists, swap the `route` here.
    if (isCorporateClient()) {
        items.push({
            label: 'Settings',
            icon: 'uil uil-setting',
            route: { name: 'me' },
        });

        // Corporate mirror of the non-corporate "Help & Support" link
        // (under Resources). Routes to the same `help-home` destination.
        // Admin-only: gate on real admin rights (Owner/Admin/Instruction
        // Admin) rather than `isCorporateAdminUser()`, which treats every
        // non-Student role (e.g. Content Editor) as "admin" for the corporate
        // view split and would leak Support to content editors.
        if (store.getters['User/hasAdminRights']) {
            items.push({
                label: 'Support',
                icon: 'uil uil-question-circle',
                route: { name: 'help-home' },
            });
        }
    }

    // Light/Dark Mode + Theme Switcher are Stanbridge-only product
    // affordances. Corporate ships a single fixed theme, so these
    // entries are hidden when EDVERUM_CLIENT=corporate. `stanbridge`
    // and an unset env value both fall through to the default and
    // keep the items visible.
    if (!isCorporateClient()) {
        items.push(
            {
                label: 'Light/Dark Mode',
                icon: isDarkTheme.value ? 'uil uil-moon' : 'uil uil-sun',
                command: () => toggleDarkMode(),
            },
            {
                label: 'Theme Switcher',
                icon: 'uil uil-palette',
                command: () => toggleConfigSidebar(),
            }
        );
    }

    items.push({
        label: translate('logout'),
        icon: 'uil uil-sign-out-alt',
        command: () => logout(),
    });

    if (isImpersonating.value) {
        items.splice(items.length - 1, 0, {
            label: isImpersonationTransitioning.value ? 'Stopping Impersonation...' : 'Stop Impersonating',
            icon: 'uil uil-sign-out-alt',
            command: () => stopImpersonation(),
        });
    }

    return items;
});

function toggleNavBar() {
    active.value = !active.value;
}

function toggleUserNavBar() {
    userActive.value = !userActive.value;
}

const edverumNavBar = ref(null);
const edverumUserNavBar = ref(null);

function documentClick(e) {
    const el = edverumNavBar.value;
    const userEl = edverumUserNavBar.value;

    const { target } = e;

    if (el && el !== target && !el.contains(target)) {
        active.value = false;
    }

    if (userEl && userEl !== target && !userEl.contains(target)) {
        userActive.value = false;
    }
}

async function setDarkPreference(theme) {
    await store.dispatch('User/setDarkPreferredTheme', theme);
}

function showUserMenu() {
    if (userMenuHideTimeout.value) {
        clearTimeout(userMenuHideTimeout.value);
        userMenuHideTimeout.value = null;
    }
    userMenuVisible.value = true;
}

function hideUserMenu() {
    userMenuHideTimeout.value = setTimeout(() => {
        userMenuVisible.value = false;
        userMenuHideTimeout.value = null;
    }, 100);
}

function toggleUserMenu() {
    userMenuVisible.value = !userMenuVisible.value;
}

function handleMenuButtonClick() {
    if (window.innerWidth < 1200) {
        toggleMenu();
    } else {
        toggleSidebarSummary();
    }
}

async function loadEnrollments() {
    const preloaded = Utilities.initialState('UserEnrollments');

    if (!preloaded || import.meta.env.VITE_APP_DEBUG === 'true') {
        const { data } = await axios.get('/api/user-enrollments');

        enrolledCourses.value = data.data.filter(enrollment => enrollment.course.is_accessible);
    }
}

watch(
    user,
    (newUser, oldUser) => {
        if (newUser && (!oldUser || oldUser.id !== newUser.id)) {
            loadEnrollments();
        }
    },
    { immediate: true }
);

onMounted(() => {
    document.addEventListener('click', documentClick);
});

onUnmounted(() => {
    document.removeEventListener('click', documentClick);
    if (userMenuHideTimeout.value) {
        clearTimeout(userMenuHideTimeout.value);
    }
});
</script>

<style scoped lang="scss">
@import '~assets/scss/toolbox.scss';

.edverum-logo-link {
    &:hover {
        background-color: var(--surface-hover);
        border-radius: 0.5rem;
    }
}
</style>
